import { Component, ErrorInfo, ReactNode } from 'react'
import { ErrorBoundaryFallback } from './ui/error/ErrorBoundaryFallback'
import { errorService } from '../services/errorService'
import { StandardError } from '../types/errorTypes'

interface Props {
  children?: ReactNode
  fallback?:
    | ReactNode
    | ((error: StandardError, resetErrorBoundary: () => void) => ReactNode)
  onError?: (
    error: Error,
    errorInfo: ErrorInfo,
    standardError?: StandardError
  ) => void
  resetOnPropsChange?: boolean
  resetKeys?: Array<string | number>
  routeName?: string
  showFallback?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  eventId: string | null
  standardError: StandardError | null
}

export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      standardError: null,
    }
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const eventId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Create a standardized error
    const standardError = errorService.processGenericError(error, {
      component: 'ErrorBoundary',
      route: this.props.routeName,
      componentStack: errorInfo.componentStack || undefined,
      eventId: eventId || undefined,
    })

    this.setState({
      error,
      errorInfo,
      eventId,
      standardError,
    })

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, standardError)
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props
    const { hasError } = this.state

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (
        resetKeys?.some((key, i) => prevProps.resetKeys?.[i] !== key) ||
        resetOnPropsChange
      ) {
        this.resetErrorBoundary()
      }
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      standardError: null,
    })
  }

  handleRetry = () => {
    this.resetErrorBoundary()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleGoBack = () => {
    window.history.back()
  }

  public render() {
    if (this.state.hasError) {
      const { fallback, showFallback = true } = this.props
      const { error, errorInfo, eventId, standardError } = this.state

      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(standardError!, this.resetErrorBoundary)
        }
        return fallback
      }

      if (!showFallback) {
        return null
      }

      // Use the new ErrorBoundaryFallback component
      return (
        <ErrorBoundaryFallback
          error={error!}
          resetErrorBoundary={this.resetErrorBoundary}
          componentStack={errorInfo?.componentStack || undefined}
          eventId={eventId !== null ? eventId : undefined}
        />
      )
    }

    return this.props.children
  }
}

// Route Error Boundary - specialized for route-level errors
export function RouteErrorBoundary({
  children,
  routeName,
}: {
  children: ReactNode
  routeName?: string
}) {
  return (
    <ErrorBoundary
      onError={(_, __, standardError) => {
        // Enhanced logging with standardized error
        if (standardError) {
          console.error(
            `Route error in ${routeName || 'unknown route'}:`,
            standardError
          )
        }
      }}
      resetKeys={routeName ? [routeName] : undefined}
      resetOnPropsChange
      routeName={routeName}
    >
      {children}
    </ErrorBoundary>
  )
}

// Chunk Load Error Boundary - specialized for code splitting errors
export function ChunkLoadErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      onError={(error, _, standardError) => {
        // Enhanced chunk load error handling
        if (
          standardError?.category === 'chunk_load' ||
          error.name === 'ChunkLoadError' ||
          error.message?.includes('Loading chunk')
        ) {
          // Log the chunk load error
          console.warn(
            'Chunk load error detected, will reload:',
            standardError || error
          )

          // Check if we've already reloaded recently to prevent reload loops
          const lastReload = sessionStorage.getItem('lastChunkReload')
          const now = Date.now()

          if (!lastReload || now - parseInt(lastReload) > 5000) {
            // Store current URL before reload to preserve navigation
            sessionStorage.setItem('lastChunkReload', now.toString())
            sessionStorage.setItem(
              'preReloadUrl',
              window.location.pathname + window.location.search
            )

            // Automatically retry chunk load errors after a short delay
            setTimeout(() => {
              window.location.reload()
            }, 1000)
          } else {
            console.error('Prevented chunk reload loop - too many reloads')
          }
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

export default ErrorBoundary
